import { css, html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Mesh, Object3D, Scene } from "three";
import { BaseElement } from "../../core/ui-kit/BaseElement";
import { observeContext } from "./SugarConfiguratorContext";
import { Engine } from "../../engine/Engine";
import { SugarModelJoint } from "../../loader/SugarModelJoint";

@customElement("sugar-scene-debug")
@observeContext("refresh")
export class SugarDebugObjectView extends BaseElement {
  @property({ type: Object }) scene?: Scene;
  @state() _expandedMap = new WeakMap();

  constructor() {
    super();
    this._expandedMap = new WeakMap();
  }

  onRefreshChanged() {
    this.scene = Engine.activeEngine.activeScene;
    this.requestUpdate();
  }

  static styles = css`
    .node {
      margin-left: 1rem;
      border-left: 1px dashed #555;
      padding-left: 0.5rem;
    }
    .header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
    }
    .toggle {
      width: 1rem;
      height: 1rem;
      line-height: 1rem;
      text-align: center;
      background: #333;
      border: 1px solid #555;
      font-size: 0.75rem;
      cursor: pointer;
    }
    .name {
      font-weight: bold;
      color: #8cf;
    }
    .type {
      color: #aaa;
      font-style: italic;
      margin-left: 0.25rem;
    }
  `;

  toggle(obj: Object3D) {
    const current = this._expandedMap.get(obj) ?? false;
    this._expandedMap.set(obj, !current);
    this.requestUpdate();
  }

  isExpanded(obj: Object3D) {
    return this._expandedMap.get(obj) ?? false;
  }

  renderNode(obj: Object3D): TemplateResult {
    const children = obj.children || [];
    const expanded = this.isExpanded(obj);
    return html`
      <div class="node">
        <div class="header" @click=${() => this.toggle(obj)}>
          <div class="toggle">
            ${children.length ? (expanded ? "−" : "+") : ""}
          </div>
          <div>
            <span class="name">${obj.name || "(unnamed)"}</span>
            <span class="name">${obj.uuid }</span>
            <span class="type">[${obj.constructor.name}]</span>
            <span class="type"
              >[${obj instanceof Mesh ? html`${obj.material.uuid}` : ``}]</span
            >
            <span class="type"
              >[${obj.position.x.toFixed(2)}, ${obj.position.y.toFixed(2)},
              ${obj.position.z.toFixed(2)}]</span
            >${obj instanceof SugarModelJoint
              ? html`<span class="type">${obj.binded}</span>`
              : ``}
          </div>
        </div>
        ${expanded
          ? html`${children.map((child) => this.renderNode(child))}`
          : ""}
      </div>
    `;
  }

  renderHtml() {
    if (!this.scene) {
      return html`<div>No scene assigned</div>`;
    }
    return html`
      <div>
        <div class="name">Scene</div>
        ${this.renderNode(this.scene)}
      </div>
    `;
  }
}
